// src/components/common/vibe/CreatePost.js
import React, { useState, useRef, useEffect } from 'react';
import { X, Image as ImageIcon, Smile, Send, MessageSquare, Camera, Lightbulb, Award, BarChart3, AtSign } from 'lucide-react';
import { useEmployees } from '../../../contexts/EmployeeContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../hooks/useAuth';
import vibeService from '../../../services/vibe.service';
import { MOODS, VISIBILITY_OPTIONS } from '../../../constants/vibeConstants';
import CreatePoll from './CreatePoll';
import EmojiPicker from './EmojiPicker';
import GifPicker from './GifPicker';

// Post type options with icons
const POST_TYPES = [
    { value: 'general_vibe', label: 'General Vibe', icon: MessageSquare, color: 'text-blue-600' },
    { value: 'photo_moment', label: 'Photo Moment', icon: Camera, color: 'text-purple-600' },
    { value: 'poll', label: 'Create Poll', icon: BarChart3, color: 'text-green-600' },
    { value: 'suggestions', label: 'Suggestions', icon: Lightbulb, color: 'text-yellow-600' }
];


const CreatePost = ({ onPostCreated, currentUser, theme }) => {
    const { employees } = useEmployees();
    const { addNotification } = useNotifications();
    const { user } = useAuth();

    const [content, setContent] = useState('');
    const [selectedMood, setSelectedMood] = useState(null);
    const [postCategory, setPostCategory] = useState('general_vibe');
    const [visibility, setVisibility] = useState('company');
    const [showMoodPicker, setShowMoodPicker] = useState(false);
    const [loading, setLoading] = useState(false);

    // Poll modal state
    const [showPollModal, setShowPollModal] = useState(false);

    // Image upload state
    const [images, setImages] = useState([]);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef(null);

    // @Mentions state
    const [mentionedEmployees, setMentionedEmployees] = useState([]);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
    const textareaRef = useRef(null);

  
    // GIF state
    const [selectedGif, setSelectedGif] = useState(null);

    // Filter employees for mention dropdown
    const filteredEmployees = employees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(mentionSearch.toLowerCase())
    );

    // Handle text change with @mention detection
    const handleTextChange = (e) => {
        const text = e.target.value;
        setContent(text);

        // Detect @ mention
        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = text.substring(0, cursorPosition);
        const words = textBeforeCursor.split(/\s/);
        const lastWord = words[words.length - 1];

        if (lastWord.startsWith('@') && lastWord.length > 1) {
            setShowMentionDropdown(true);
            setMentionSearch(lastWord.substring(1));

            // Calculate dropdown position
            const textarea = textareaRef.current;
            if (textarea) {
                const rect = textarea.getBoundingClientRect();
                setMentionPosition({ top: rect.bottom, left: rect.left });
            }
        } else {
            setShowMentionDropdown(false);
        }
    };

    // Handle mention selection
    const handleMentionSelect = (employee) => {
        const cursorPosition = textareaRef.current.selectionStart;
        const textBeforeCursor = content.substring(0, cursorPosition);
        const textAfterCursor = content.substring(cursorPosition);

        // Replace the @search with @FullName
        const words = textBeforeCursor.split(/\s/);
        words[words.length - 1] = `@${employee.first_name} ${employee.last_name}`;
        const newText = words.join(' ') + ' ' + textAfterCursor;

        setContent(newText);
        setShowMentionDropdown(false);

        // Add to mentioned employees if not already added
        if (!mentionedEmployees.includes(employee.id)) {
            setMentionedEmployees([...mentionedEmployees, employee.id]);
        }

        // Focus back on textarea
        textareaRef.current.focus();
    };

    // Handle emoji selection
    const handleEmojiSelect = (emoji) => {
        const cursorPosition = textareaRef.current.selectionStart || content.length;
        const textBeforeCursor = content.substring(0, cursorPosition);
        const textAfterCursor = content.substring(cursorPosition);
        const newText = textBeforeCursor + emoji + textAfterCursor;
        setContent(newText);
        textareaRef.current.focus();
    };

    // Handle GIF selection
    const handleGifSelect = (gif) => {
        setSelectedGif(gif);
    };

    // Handle image upload
    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploadingImage(true);
        try {
            const uploadedUrls = [];
            for (const file of files) {
                const result = await vibeService.uploadImage(file);
                uploadedUrls.push(result.data.image_url);
            }
            setImages([...images, ...uploadedUrls]);
        } catch (error) {
            console.error('Error uploading images:', error);
            alert('Failed to upload images. Please try again.');
        } finally {
            setUploadingImage(false);
        }
    };

    // Remove image
    const handleRemoveImage = (index) => {
        setImages(images.filter((_, i) => i !== index));
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content.trim() && images.length === 0 && !selectedGif) return;

        setLoading(true);
        try {
            const postData = {
                content: content.trim(),
                post_type: postCategory, // Primary field that backend recognizes
                mood: selectedMood,
                visibility: visibility,
                mentioned_employees: mentionedEmployees.length > 0 ? mentionedEmployees : null
            };

            console.log('📝 Creating post with base data:', {
                postCategory,
                postData: {
                    ...postData,
                    is_suggestion: postCategory === 'suggestions'
                }
            });

            // Add category-specific fields
            if (postCategory === 'photo_moment' || images.length > 0) {
                // Send image URLs for photo moments
                if (images.length > 0) {
                    postData.image_url = images[0]; // Single image for backward compatibility
                    postData.image_urls = images; // Multiple images for new format
                    console.log('🖼️ Creating post with images:', {
                        postCategory,
                        imagesCount: images.length,
                        imageUrls: images,
                        postData: {
                            ...postData,
                            image_url: images[0],
                            image_urls: images
                        }
                    });
                }
            }

            // Add GIF if selected
            if (selectedGif) {
                postData.gif_url = selectedGif.url;
                postData.gif_title = selectedGif.title;
            }


            const createdPost = await onPostCreated(postData);

            // Send notifications to mentioned employees
            if (mentionedEmployees.length > 0) {
                const postPreview = content.length > 100 ? content.substring(0, 100) + '...' : content;

                for (const employeeId of mentionedEmployees) {
                    // Don't notify the author
                    if (employeeId !== (user?.id || currentUser?.id)) {
                        await addNotification({
                            userId: employeeId,
                            type: 'vibe_mention',
                            title: 'You were mentioned in Vibe',
                            message: `${user?.name || currentUser?.first_name + ' ' + currentUser?.last_name || 'Someone'} mentioned you in a post: "${postPreview}"`,
                            icon: 'MessageSquare',
                            color: 'purple',
                            data: {
                                post_id: createdPost?.id || createdPost?.data?.id,
                                mentioned_by: user?.name || currentUser?.first_name + ' ' + currentUser?.last_name,
                                post_content: postPreview
                            }
                        });
                    }
                }
            }

            // Reset form
            setContent('');
            setSelectedMood(null);
            setImages([]);
            setSelectedGif(null);
            setMentionedEmployees([]);
            setMentionedEmployees([]);
            setShowMoodPicker(false);
        } catch (error) {
            console.error('Error creating post:', error);
            alert('Failed to create post. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const selectedMoodData = MOODS.find(m => m.value === selectedMood);

    return (
        <>
            {/* Poll Modal - Outside the form to avoid nested form issue */}
            <CreatePoll
                isOpen={showPollModal}
                onClose={() => setShowPollModal(false)}
                onPollCreated={onPostCreated}
                theme={theme}
            />

            <div className={`rounded-xl p-6 border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="flex items-start gap-4">
                    {/* User Avatar */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                        theme === 'dark' ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'
                    }`}>
                        {currentUser?.first_name?.[0] || 'U'}
                    </div>

                    {/* Post Form */}
                    <form onSubmit={handleSubmit} className="flex-1">
                        <div className="space-y-3">
                            {/* Post Type Selection */}
                            <div className="flex gap-2">
                                {POST_TYPES.map((type) => {
                                    const Icon = type.icon;
                                    return (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() => {
                                                if (type.value === 'poll') {
                                                    setShowPollModal(true);
                                                } else {
                                                    setPostCategory(type.value);
                                                }
                                            }}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                                postCategory === type.value
                                                    ? 'bg-blue-600 text-white'
                                                    : theme === 'dark'
                                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            {type.label}
                                        </button>
                                    );
                                })}
                            </div>

                        {/* Photo Moment Upload Area */}
                        {postCategory === 'photo_moment' && (
                            <div className={`p-6 rounded-lg border-2 border-dashed ${
                                theme === 'dark'
                                    ? 'border-gray-600 bg-gray-700/50'
                                    : 'border-gray-300 bg-gray-50'
                            }`}>
                                <div className="text-center">
                                    <Camera className={`w-12 h-12 mx-auto mb-3 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                    }`} />
                                    <h3 className={`text-lg font-medium mb-2 ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        Share Your Photo Moment
                                    </h3>
                                    <p className={`text-sm mb-4 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        Upload one or more photos to share with your team
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingImage}
                                        className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto ${
                                            uploadingImage
                                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                        }`}
                                    >
                                        {uploadingImage ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <ImageIcon className="w-5 h-5" />
                                                Choose Photos
                                            </>
                                        )}
                                    </button>
                                    <p className={`text-xs mt-2 ${
                                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                    }`}>
                                        or drag and drop images here
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Textarea with relative positioning for mentions */}
                        {(postCategory !== 'photo_moment' || images.length > 0) && (
                            <div className="relative">
                                <textarea
                                    ref={textareaRef}
                                    value={content}
                                    onChange={handleTextChange}
                                    placeholder={
                                        postCategory === 'suggestions'
                                            ? "Share your suggestion or feedback..."
                                            : postCategory === 'photo_moment'
                                                ? "Add a caption to your photos (optional)..."
                                                : "What's on your mind? Share your vibe... (use @name to mention someone)"
                                    }
                                    rows={postCategory === 'photo_moment' ? 2 : 3}
                                    className={`w-full px-4 py-3 rounded-lg border resize-none ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                />

                                {/* Mention Dropdown */}
                                {showMentionDropdown && filteredEmployees.length > 0 && (
                                    <div className={`absolute z-50 mt-1 w-64 max-h-48 overflow-y-auto rounded-lg border shadow-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600'
                                            : 'bg-white border-gray-200'
                                    }`}>
                                        {filteredEmployees.slice(0, 5).map((emp) => (
                                            <button
                                                key={emp.id}
                                                type="button"
                                                onClick={() => handleMentionSelect(emp)}
                                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 ${
                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                }`}
                                            >
                                                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                                    {emp.first_name[0]}{emp.last_name[0]}
                                                </div>
                                                <div>
                                                    <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                                                    <div className="text-xs opacity-75">{emp.designation}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        
                        {/* Image Previews */}
                        {images.length > 0 && (
                            <div className={`p-4 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600'
                                    : 'bg-gray-50 border-gray-200'
                            }`}>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className={`text-sm font-medium ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        {images.length === 1 ? '1 Photo' : `${images.length} Photos`}
                                    </h4>
                                    {postCategory === 'photo_moment' && (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className={`text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1`}
                                        >
                                            <ImageIcon className="w-4 h-4" />
                                            Add More
                                        </button>
                                    )}
                                </div>
                                <div className={`grid gap-3 ${
                                    images.length === 1 ? 'grid-cols-1' :
                                    images.length === 2 ? 'grid-cols-2' :
                                    'grid-cols-3'
                                }`}>
                                    {images.map((imageUrl, index) => (
                                        <div key={index} className="relative group">
                                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-600">
                                                <img
                                                    src={imageUrl}
                                                    alt={`Upload ${index + 1}`}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                            {postCategory === 'photo_moment' && (
                                                <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                                                    Photo {index + 1}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* GIF Preview */}
                        {selectedGif && (
                            <div className={`p-3 rounded-lg border ${
                                theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                            }`}>
                                <div className="flex items-start gap-3">
                                    <img
                                        src={selectedGif.url}
                                        alt={selectedGif.title}
                                        className="w-32 h-32 rounded object-cover"
                                    />
                                    <div className="flex-1">
                                        <p className={`text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {selectedGif.title || 'Selected GIF'}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedGif(null)}
                                            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                                        >
                                            <X className="w-4 h-4" />
                                            Remove GIF
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Selected Mood Display */}
                        {selectedMoodData && (
                            <div className="flex items-center gap-2">
                                <span className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 ${selectedMoodData.color}`}>
                                    <span className="text-lg">{selectedMoodData.emoji}</span>
                                    Feeling {selectedMoodData.label}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedMood(null)}
                                        className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            </div>
                        )}

                        {/* Mentioned Employees Display */}
                        {mentionedEmployees.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-medium ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Mentioning:
                                </span>
                                {mentionedEmployees.map((empId) => {
                                    const emp = employees.find(e => e.id === empId);
                                    if (!emp) return null;
                                    return (
                                        <span
                                            key={empId}
                                            className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${
                                                theme === 'dark'
                                                    ? 'bg-blue-900/30 text-blue-400'
                                                    : 'bg-blue-100 text-blue-700'
                                            }`}
                                        >
                                            @{emp.first_name} {emp.last_name}
                                            <button
                                                type="button"
                                                onClick={() => setMentionedEmployees(mentionedEmployees.filter(id => id !== empId))}
                                                className="hover:bg-black/10 dark:hover:bg-white/10 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Mood Picker */}
                        {showMoodPicker && (
                            <div className={`p-4 rounded-lg border ${
                                theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                            }`}>
                                <p className={`text-sm font-medium mb-3 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    How are you feeling?
                                </p>
                                <div className="grid grid-cols-5 gap-2">
                                    {MOODS.map((mood) => (
                                        <button
                                            key={mood.value}
                                            type="button"
                                            onClick={() => {
                                                setSelectedMood(mood.value);
                                                setShowMoodPicker(false);
                                            }}
                                            className={`p-3 rounded-lg text-center transition-all ${
                                                selectedMood === mood.value
                                                    ? mood.color
                                                    : theme === 'dark'
                                                        ? 'bg-gray-600 hover:bg-gray-500'
                                                        : 'bg-white hover:bg-gray-100'
                                            }`}
                                            title={mood.label}
                                        >
                                            <div className="text-2xl mb-1">{mood.emoji}</div>
                                            <div className="text-xs font-medium">{mood.label}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action Bar */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {/* Emoji Picker */}
                                <EmojiPicker
                                    onEmojiSelect={handleEmojiSelect}
                                    theme={theme}
                                />

                                {/* GIF Picker */}
                                <GifPicker
                                    onGifSelect={handleGifSelect}
                                    theme={theme}
                                />

                                {/* Mood Button */}
                                {postCategory !== 'suggestions' && (
                                    <button
                                        type="button"
                                        onClick={() => setShowMoodPicker(!showMoodPicker)}
                                        className={`p-2 rounded-lg transition-colors ${
                                            showMoodPicker
                                                ? 'bg-blue-600 text-white'
                                                : theme === 'dark'
                                                    ? 'text-gray-400 hover:bg-gray-700'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        title="Add mood"
                                    >
                                        <Smile className="w-5 h-5" />
                                    </button>
                                )}

                                {/* Image Upload Button */}
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingImage}
                                    className={`p-2 rounded-lg transition-colors ${
                                        uploadingImage
                                            ? 'opacity-50 cursor-not-allowed'
                                            : theme === 'dark'
                                                ? 'text-gray-400 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                    title="Add image"
                                >
                                    {uploadingImage ? (
                                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <ImageIcon className="w-5 h-5" />
                                    )}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />

                                {/* Mention Button */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setContent(content + '@');
                                        textareaRef.current?.focus();
                                    }}
                                    className={`p-2 rounded-lg transition-colors ${
                                        theme === 'dark'
                                            ? 'text-gray-400 hover:bg-gray-700'
                                            : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                    title="Mention someone"
                                >
                                    <AtSign className="w-5 h-5" />
                                </button>

                                {/* Visibility Selector */}
                                {postCategory !== 'suggestions' && (
                                    <select
                                        value={visibility}
                                        onChange={(e) => setVisibility(e.target.value)}
                                        className={`px-3 py-1.5 rounded-lg border text-sm ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    >
                                        {VISIBILITY_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.icon} {option.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={(!content.trim() && images.length === 0 && !selectedGif) || loading}
                                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                                    (content.trim() || images.length > 0 || selectedGif) && !loading
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                        : theme === 'dark'
                                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                            >
                                {loading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Posting...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Post
                                    </>
                                )}
                            </button>
                        </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default CreatePost;
